import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, UiCard, UiCardHeader, UiCardBody, UiDataTable, UiTableFilters, ModalPasswordConfirmation } from 'components/common'
import { Box, Button, Checkbox, Flex, Heading, Menu, MenuButton, MenuItem, MenuList, useDisclosure } from '@chakra-ui/react'
import { MdSettings, MdFileDownload } from 'react-icons/md'
import { deleteProject, filterProjects, exportProjects } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { ModalNotes } from './ModalNotes'
import {
  ALL_PROJECTS_FILTER_FIELDS, ALL_PROJECTS_COLUMNS,
  STAGING_PROJECTS_COLUMNS, STAGING_PROJECTS_FILTER_FIELDS,
  PRE_PRODUCTION_PROJECTS_COLUMNS, PRE_PRODUCTION_PROJECTS_FILTER_FIELDS,
  PRODUCTION_PROJECTS_COLUMNS, PRODUCTION_PROJECTS_FILTER_FIELDS,
  SHIPPING_PROJECTS_COLUMNS, SHIPPING_PROJECTS_FILTER_FIELDS,
  INSTALL_PROJECTS_COLUMNS, INSTALL_PROJECTS_FILTER_FIELDS,
  POST_INSTALL_PROJECTS_COLUMNS, POST_INSTALL_PROJECTS_FILTER_FIELDS,
  INVOICING_PROJECTS_COLUMNS, INVOICING_PROJECTS_FILTER_FIELDS,
  MARGIN_REVIEW_PROJECTS_COLUMNS, MARGIN_REVIEW_PROJECTS_FILTER_FIELDS,
  COMPLETE_PROJECTS_COLUMNS, COMPLETE_PROJECTS_FILTER_FIELDS,
  CANCELLED_PROJECTS_COLUMNS, CANCELLED_PROJECTS_FILTER_FIELDS,
  ON_HOLD_PROJECTS_COLUMNS, ON_HOLD_PROJECTS_FILTER_FIELDS
} from './data'
import { useDebouncedCallback } from 'use-debounce'
import { PROJECT_STAGES, getAssetUrl, ROLES } from 'lib'
import { useSearchParams } from 'react-router-dom'

const BreadcrumbNav = [
  {
    label: 'Projects',
    isCurrentPage: true
  }
]

export const ProjectList = ({ isMy = false }) => {
  const [searchParams] = useSearchParams()
  const queryTab = searchParams.get('tab')
  const disclosureRemove = useDisclosure()
  const disclosureNotes = useDisclosure()
  const { currentUser, createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 50,
    search: '',
    orderBy: {
      key: 'id',
      direction: 'desc'
    }
  })
  const [filterFieldValues, setFilterFieldValues] = useState([]) // e.g. [{ field: 'fabRend', name: 'Job Packet', values: [{ value: 'Incomplete', count: 10 }] }]
  const [filterSelections, setFilterSelections] = useState({}) // e.g. { fabRend: ['Incomplete', 'Pending Review', 'Competed'] }
  const [selectedTab, setSelectedTab] = useState('All')
  const [isLoading, setIsLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [selectedProjectForNotes, setSelectedProjectForNotes] = useState(null)
  const [selectedProjectForRemove, setSelectedProjectForRemove] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  // Column visibility state - default all toggleable columns to visible
  const [visibleColumns, setVisibleColumns] = useState({
    'Order Date': true,
    // eslint-disable-next-line quote-props
    'State': true,
    'Sales User': true
  })

  useEffect(() => {
    if (queryTab) {
      setSelectedTab(queryTab)
    } else {
      setSelectedTab('All')
    }
  }, [queryTab])

  const load = async () => {
    setIsLoading(true)
    setRows([])
    try {
      const response = await filterProjects(
        selectedTab,
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction,
        getTableColumnsAndFilterFieldsByTab(selectedTab).filterFields,
        selectedTab === 'All' ? { ...filterSelections, salesUserIds: isMy ? [currentUser.id] : [] } : { ...filterSelections, stage: [selectedTab], salesUserIds: isMy ? [currentUser.id] : [] }
      )
      setRows(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setFilterFieldValues(response.filterFieldValues)
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  const debouncedLoad = useDebouncedCallback(load, 400)

  useEffect(() => {
    debouncedLoad()
  }, [isMy, tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction, selectedTab, filterSelections])

  const onRemove = (project) => {
    setSelectedProjectForRemove(project)
    disclosureRemove.onOpen()
  }

  const handleRemove = async (projectId) => {
    try {
      await deleteProject(projectId)
      disclosureRemove.onClose()
      load()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
    }
  }

  const onNotes = (project) => {
    setSelectedProjectForNotes(project)
    disclosureNotes.onOpen()
  }

  const onView = (project) => {
    window.open(`/projects/detail/${project.id}`, '_blank')
  }

  const customizedRows = useMemo(() => {
    return rows.map(v => ({ ...v, currentUser, onRemove, onView, onNotes }))
  }, [rows])

  const updateFilterSelections = (field, value) => {
    const newFilterSelections = { ...filterSelections }
    if (newFilterSelections[field]) {
      if (newFilterSelections[field].includes(value)) {
        newFilterSelections[field] = newFilterSelections[field].filter(v => v !== value)
      } else {
        newFilterSelections[field].push(value)
      }
    } else {
      newFilterSelections[field] = [value]
    }
    setFilterSelections(newFilterSelections)
  }

  const handleChangeTab = (v) => {
    setSelectedTab(v)
    setFilterSelections({})
  }

  const getOriginalColumnsByTab = (tab) => {
    switch (tab) {
      case 'All':
        return ALL_PROJECTS_COLUMNS
      case PROJECT_STAGES.Staging:
        return STAGING_PROJECTS_COLUMNS
      case PROJECT_STAGES.PreProduction:
        return PRE_PRODUCTION_PROJECTS_COLUMNS
      case PROJECT_STAGES.Production:
        return PRODUCTION_PROJECTS_COLUMNS
      case PROJECT_STAGES.Shipping:
        return SHIPPING_PROJECTS_COLUMNS
      case PROJECT_STAGES.Install:
        return INSTALL_PROJECTS_COLUMNS
      case PROJECT_STAGES.PostInstall:
        return POST_INSTALL_PROJECTS_COLUMNS
      case PROJECT_STAGES.Invoicing:
        return INVOICING_PROJECTS_COLUMNS
      case PROJECT_STAGES.MarginReview:
        return MARGIN_REVIEW_PROJECTS_COLUMNS
      case PROJECT_STAGES.Complete:
        return COMPLETE_PROJECTS_COLUMNS
      case PROJECT_STAGES.Cancelled:
        return CANCELLED_PROJECTS_COLUMNS
      case PROJECT_STAGES.OnHold:
        return ON_HOLD_PROJECTS_COLUMNS
      default:
        return ALL_PROJECTS_COLUMNS
    }
  }

  const getTableColumnsAndFilterFieldsByTab = (tab) => {
    const getFilterFields = () => {
      switch (tab) {
        case 'All':
          return ALL_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.Staging:
          return STAGING_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.PreProduction:
          return PRE_PRODUCTION_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.Production:
          return PRODUCTION_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.Shipping:
          return SHIPPING_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.Install:
          return INSTALL_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.PostInstall:
          return POST_INSTALL_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.Invoicing:
          return INVOICING_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.MarginReview:
          return MARGIN_REVIEW_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.Complete:
          return COMPLETE_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.Cancelled:
          return CANCELLED_PROJECTS_FILTER_FIELDS
        case PROJECT_STAGES.OnHold:
          return ON_HOLD_PROJECTS_FILTER_FIELDS
        default:
          return ALL_PROJECTS_FILTER_FIELDS
      }
    }

    // Filter columns based on visibility for all tabs
    const columns = getOriginalColumnsByTab(tab)
    const filteredColumns = columns.filter(col => !col.toggleable || visibleColumns[col.label] !== false)

    return {
      columns: filteredColumns,
      filterFields: getFilterFields()
    }
  }

  const tableFilterPlaceholder = useMemo(() => {
    if (selectedTab === PROJECT_STAGES.Production) {
      return 'Not Yet Determined'
    }
    if (selectedTab === PROJECT_STAGES.Shipping) {
      return 'Pending'
    }
    return 'No data'
  }, [selectedTab])

  const columnVisibilityMenu = useMemo(() => {
    // Get original columns for current tab to check if any are toggleable
    const originalColumns = getOriginalColumnsByTab(selectedTab)
    const toggleableColumns = originalColumns.filter(col => col.toggleable)

    // Only show menu if there are toggleable columns
    if (toggleableColumns.length === 0) return null

    // Get available toggleable column labels for current tab
    const availableColumnLabels = toggleableColumns.map(col => col.label)

    return (
      <Menu closeOnSelect={false}>
        <MenuButton as={Button} size="xs" leftIcon={<MdSettings />}>
          Columns
        </MenuButton>
        <MenuList>
          {availableColumnLabels.map(columnLabel => (
            <MenuItem
              key={columnLabel}
              onClick={(e) => {
                e.stopPropagation()
                setVisibleColumns(prev => ({
                  ...prev,
                  [columnLabel]: !prev[columnLabel]
                }))
              }}
              closeOnSelect={false}
            >
              <Checkbox
                isChecked={visibleColumns[columnLabel] !== false}
                onChange={() => {
                  setVisibleColumns(prev => ({
                    ...prev,
                    [columnLabel]: !prev[columnLabel]
                  }))
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {columnLabel}
              </Checkbox>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    )
  }, [selectedTab, visibleColumns])

  const handleExportCSV = async () => {
    setIsExporting(true)
    try {
      const response = await exportProjects({
        tab: selectedTab,
        search: tableConfig.search,
        orderByKey: tableConfig.orderBy.key,
        direction: tableConfig.orderBy.direction,
        filterFields: getTableColumnsAndFilterFieldsByTab(selectedTab).filterFields,
        filterSelections: selectedTab === 'All' ? { ...filterSelections, salesUserIds: isMy ? [currentUser.id] : [] } : { ...filterSelections, stage: [selectedTab], salesUserIds: isMy ? [currentUser.id] : [] }
      })

      // Download the CSV file from the backend
      const link = document.createElement('a')
      link.href = getAssetUrl(response.path)
      link.download = `projects_${selectedTab}_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      createToast('success', 'Export Successful', 'Projects exported to CSV successfully')
    } catch (err) {
      console.error(err)
      createToast('error', 'Export Failed', err.response?.data || 'Failed to export projects')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Projects</Heading>
        </Flex>
        <UiCard borderRadius={0}>
          <UiCardHeader bg="gray.500" p={4}>
            <Flex gap={2} justifyContent="space-between" alignItems="center">
              <Flex gap={2}>
                <a href={'/projects?tab=All'}>
                  <Button
                    border={1}
                    borderColor="gray.200"
                    borderStyle="solid"
                    colorScheme={selectedTab === 'All' ? 'blue' : 'gray'}
                    size="xs"
                    borderRadius="4px"
                    onClick={() => handleChangeTab('All')}
                  >
                    All
                  </Button>
                </a>
                {Object.keys(PROJECT_STAGES).map(v =>
                  <a
                    key={PROJECT_STAGES[v]}
                    href={'/projects?tab=' + PROJECT_STAGES[v]}
                  >
                    <Button
                      border={1}
                      borderColor="gray.200"
                      borderStyle="solid"
                      colorScheme={selectedTab === PROJECT_STAGES[v] ? 'blue' : 'gray'}
                      size="xs"
                      borderRadius="4px"
                      onClick={() => handleChangeTab(PROJECT_STAGES[v])}
                    >
                      {PROJECT_STAGES[v]}
                    </Button>
                  </a>
                )}
              </Flex>
              <Flex gap={2}>
                {columnVisibilityMenu}
                {currentUser?.role === ROLES.Admin && (
                  <Button
                    size="xs"
                    leftIcon={<MdFileDownload />}
                    onClick={handleExportCSV}
                    isLoading={isExporting}
                    loadingText="Exporting..."
                    colorScheme="blue"
                  >
                    Export CSV
                  </Button>
                )}
              </Flex>
            </Flex>
          </UiCardHeader>
          <UiCardBody p={8}>
            {filterFieldValues.length > 0 && (
              <UiTableFilters
                filterFieldValues={filterFieldValues}
                filterSelections={filterSelections}
                updateFilterSelections={updateFilterSelections}
                placeholderValue={tableFilterPlaceholder}
                mb={4}
              />
            )}
            <UiDataTable
              tableConfig={tableConfig}
              updateTableConfig={setTableConfig}
              rows={customizedRows}
              rowKey="id"
              columns={getTableColumnsAndFilterFieldsByTab(selectedTab).columns}
              isLoading={isLoading}
            />
          </UiCardBody>
        </UiCard>
      </Box>
      <ModalPasswordConfirmation
        heading={selectedProjectForRemove?.customerCompany}
        btnConfirmLabel="Remove"
        onConfirm={() => handleRemove(selectedProjectForRemove?.id)}
        {...disclosureRemove}
      />
      <ModalNotes
        project={selectedProjectForNotes}
        reload={load}
        {...disclosureNotes}
      />
    </>
  )
}

ProjectList.propTypes = {
  isMy: PropTypes.bool
}
